import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  Autocomplete,
  CircularProgress,
  FormControl,
  Link,
  ListItem,
  TextField
} from '@mui/material';
import clientGraphql from '../../../../common/apollo-graphql';
import AuthContext from '../../../auth/context/AuthContext';
import { GET_CLINIC } from '../../../clinic/gql/Query';
import AddClinicProviderDialog from '../../../clinics/Management/ClinicDetails/AddClinicProviderDialog';
const ADD_OPTION = {
    id: 'ADD',
    providerName: 'Can\'t find provider? Click here to add',
};

function useProviderDropdownOptions({clinicId, value, onOptionsLoaded=null}) {

    const [options, setOptions] = useState([]);

    const { loading, data, refetch } = useQuery(GET_CLINIC, {
    client: clientGraphql,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: clinicId ? { id: clinicId } : undefined,
    skip: !clinicId,
  });

    const buildOptions = useCallback((dataObj) => {
      const list = (dataObj?.clinic?.providers ?? []).flatMap((provider) =>
        (provider.clinics || []).map((clinic) => ({
          id: provider.id,
          providerName: provider.display_name,
          firstname: provider.firstname,
          lastname: provider.lastname,
          clinicId: dataObj?.clinic?.id,
          clinicName: dataObj?.clinic?.name,
        }))
      );
      return list;
    }, []);

    // Find the selected provider object by ID (reactively)
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        if (options && Array.isArray(options)) {
            const newSelected = options.find((provider) => String(provider.id) === String(value)) || null;
            setSelected(newSelected);
        } else {
            setSelected(null);
        }
    }, [options, value]);

    useEffect(() => {
      if (data) {
        const providerClinicOptions = buildOptions(data);
        setOptions([ADD_OPTION, ...providerClinicOptions]);
        if (typeof onOptionsLoaded === 'function') {
          onOptionsLoaded(providerClinicOptions);
        }
      }
      return () => setOptions([]);
    }, [data, onOptionsLoaded, buildOptions]);

    const reloadOptions = useCallback(async () => {
      const res = await refetch();
      const fresh = buildOptions(res.data);
      setOptions([ADD_OPTION, ...fresh]);
      if (typeof onOptionsLoaded === 'function') {
        onOptionsLoaded(fresh);
      }
      return fresh;
    }, [refetch, buildOptions, onOptionsLoaded]);

    return {
        options,
        selected,
        loading,
        refetch,
        reloadOptions,
    };
}

const ProviderDropdown = ({ clinic, isReceiver=false, value, onChange, label, error, required, helperText, defaultName=null, openDialogOverride=null,onOptionsLoaded=null }) => {
  const [inputValue, setInputValue] = useState('');
    const [dialogData, setDialogData] = useState({open: false, name: ''});
  
    const currentUser = useContext(AuthContext);

  const currentUserClinicId = currentUser?.clinic?.id;
  const clinicId = isReceiver ? currentUserClinicId : (clinic?.id || null);
    const toggleDialog = useCallback((open, name='') => {
        setDialogData(prevState => ({...prevState, name, open}));
    }, [setDialogData]);
  
    const {options, selected, loading, refetch, reloadOptions} = useProviderDropdownOptions({clinicId, value, onOptionsLoaded});

    const onCloseDialog = useCallback(() => {
        toggleDialog(false);
        if (typeof reloadOptions === 'function') {
          reloadOptions();
        }
    }, [toggleDialog, reloadOptions]);
  
    const onSaveClinicSuccess = useCallback((response) => {
        // trigger onChange to select the current added clinic
        onChange({
            target: {
                value: response.id,
            },
        });
    }, [onChange]);


  

  useEffect(() => {
        if (defaultName && openDialogOverride) {
            toggleDialog(true, defaultName);
        }
    }, [defaultName, openDialogOverride, toggleDialog]);

  return (
    <>
    <FormControl fullWidth error={error}>
      <Autocomplete
        disabled={!clinicId}
        options={options}
        loading={loading}
        getOptionLabel={(option) => {
          const hasClinic = option.clinicName && option.clinicName.trim() !== '';
          return hasClinic ? `${option.providerName} - ${option.clinicName}` : option.providerName;
        }}
        value={selected}
        onChange={(e, newValue) => {
          const id = newValue?.id ?? null;
          if (id === ADD_OPTION.id) {
                setInputValue('');
                setTimeout(() => {
                    toggleDialog(true);
                });
            } 
          else if (typeof onChange === 'function') {
            onChange({ target: { value: id, option: newValue } });
          }
        }}
        isOptionEqualToValue={(option, val) => option.id === val.id}
        disableClearable={required}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            error={error}
            helperText={helperText}
            required={required}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              )
            }}
          />
        )}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          if (option.id === ADD_OPTION.id || Object.prototype.hasOwnProperty.call(option, 'inputValue')) {
            return (
              <ListItem key={key} {...optionProps} sx={{ color: (theme) => theme.palette.info.main }}>
                <Link href="#" color="inherit">{option.providerName}</Link>
              </ListItem>
            );
          }
          const hasClinic = option.clinicName && option.clinicName.trim() !== '';
          const label = hasClinic ? `${option.providerName} - ${option.clinicName}` : option.providerName;
          return (
            <ListItem key={key} {...optionProps}>
              {label}
            </ListItem>
          );
        }}
      />
    </FormControl>
    <AddClinicProviderDialog clinicId={clinicId}
          open={dialogData.open}
          onClose={onCloseDialog} />
          </>
    
  );
};

export default ProviderDropdown;